import * as React from "react";
import styles from "./styles.module.css";
import { useQuery } from "@tanstack/react-query";
import { getPatientGeneralDetails } from "../../../services/patient/details";
import { getAuditReport, getSummary } from "../../../services/case-manager/billings";
import api from "../../../services/axios";
import {
  formatDate,
  formatSecondsToTime,
  convertUTCToLocal
} from "../../../utils/helper";
import { formatUSADate, formatUSADateTime, formatReportMonth, parseDateSafe, formatNaiveDateTime } from "../../../utils/dateFormatters";
import Button from "../../button-v1";
import { pdf } from "@react-pdf/renderer";
import { saveAs } from "file-saver";
import toast from "react-hot-toast";
import { useSearchParams } from "react-router-dom";
import Spinner from "../../spinner";
import logo from "../../../assets/images/toca_health_pdf_logo.png";
import AuditReportPDF from "../case-manager-audit-report/AuditReportPDF";
import { buildReportFilename, DOCTYPES } from "../../../utils/fileNaming";

const ReadingView = () => {
  const [queryParams] = useSearchParams();
  const patientId = queryParams.get("patientId");
  const auditType = queryParams.get("auditType") || "rpm";
  const date = queryParams.get("date");
  const [billingData, setBillingData] = React.useState(null);

  const { data: patient, isLoading } = useQuery(
    ["patient-general-details", patientId],
    () => getPatientGeneralDetails(patientId)
  );

  const locationId = patient?.location?.id;
  const accountId = sessionStorage.getItem("acc_id");

  // Fetch Assigned Devices
  const { data: assignedDevices } = useQuery(
    ["assigned-devices", patientId],
    async () => {
      if (!patientId || !accountId) return [];
      const response = await api.get(
        `/devices/${accountId}/assigned-devices/${patientId}/`
      );
      return response.data;
    },
    {
      enabled: !!patientId && !!accountId,
    }
  );

  // Fetching billing summary to get Codes, Units, Description
  const { isLoading: isBillingLoading } = useQuery(
    ["billing-summary", date, auditType, locationId],
    () => {
       const dateStr = date ? `${date}` : new Date().toISOString().slice(0, 7); 
       return getSummary(dateStr, auditType || "all", locationId, undefined, undefined, undefined, 1, false);
    },
    {
      enabled: !!locationId && !!date,
      onSuccess: (data) => {
        if (data?.patients) {
          // Finding the specific patient in the list
          const foundPatient = data.patients.find((p) => {
             const apiId = String(p.patient_id).replace(/^P/, "");
             const currentId = String(patientId).replace(/^P/, "");
             return apiId === currentId;
          });
          setBillingData(foundPatient || null);
        }
      },
    }
  );

  const { data: auditReport, isLoading: isReadingLoading } = useQuery(
    ["patient-readings", patientId, date],
    () => getAuditReport(patientId, date),
    {
      enabled: !!patientId && !!date,
      select: data => {
        if (!auditType) {
          return data;
        }

        return {
          ...data,
          monitoring_sessions:
            data.monitoring_sessions
              ?.filter(session => {
                if (auditType === "rpm") return Number(session.total_rpm_seconds) > 0;
                if (auditType === "ccm") return Number(session.total_ccm_seconds) > 0;
                return true;
              })
              .filter(Boolean)
              .map(({ total_rpm_seconds, total_ccm_seconds, ...session }) => {
                if (auditType === "rpm") {
                  return { ...session, total_rpm_seconds };
                } else if (auditType === "ccm") {
                  return { ...session, total_ccm_seconds };
                }
                return { ...session, total_rpm_seconds, total_ccm_seconds };
              }) ?? []
        };
      }
    }
  );

  // Helper to generate chronological Event Log
  const generateEvents = (report, type) => {
    if (!report) return [];

    const events = [];

    // Add Readings
    report.readings?.forEach((r) => {
      let details = "";
      if (r.systolic || r.diastolic) {
        details = `Blood Pressure: ${r.systolic || 0}/${r.diastolic || 0} mm Hg${r.heart_rate ? ` / ${r.heart_rate} bpm` : ""}`;
      } else if (r.weight) {
        details = `Weight: ${r.weight} kg`;
      } else if (r.glucose) {
        details = `Glucose: ${r.glucose} mg/dl`;
      } else if (r.temperature) {
        details = `Temperature: ${r.temperature} F`;
      } else if (r.oxygen) {
        details = `Oxygen: ${r.oxygen}%${r.heart_rate ? ` / ${r.heart_rate} bpm` : ""}`;
      } else {
        details = `Value: ${r.value_1 || "--"}`;
      }

      events.push({
        date: r.recorded_at,
        type: "Reading",
        details: details,
        creator: "Device"
      });
    });

    // Add Notes
    report.notes?.forEach((n) => {
      events.push({
        date: n.created_at,
        type: "Note",
        details: n.note_text,
        creator: n.assigned_by_name || n.assigned_by || "--"
      });
    });

    // Add Monitoring Sessions - use correct duration based on audit type
    report.monitoring_sessions?.forEach((s) => {
      const duration = type === "ccm"
        ? s.total_ccm_seconds
        : s.total_rpm_seconds;

      events.push({
        date: s.created_at,
        type: "Monitoring Session",
        details: `Duration: ${formatSecondsToTime(duration)}`,
        creator: s.assigned_by_name || s.staff_member?.full_name || "--"
      });
    });

    // Sort Chronologically (Oldest First)
    return events.sort((a, b) => (parseDateSafe(a.date) || new Date(0)) - (parseDateSafe(b.date) || new Date(0)));
  };

  const eventLog = React.useMemo(() => generateEvents(auditReport, auditType), [auditReport, auditType]);

  // Calculate monthly monitoring time with fallback to sum of sessions if API doesn't return total_rpm_time
  const monthlyRpmSeconds = React.useMemo(() => {
    if (auditReport?.total_rpm_time) return auditReport.total_rpm_time;
    return auditReport?.monitoring_sessions?.reduce(
      (sum, session) => sum + (session.total_rpm_seconds || 0), 0
    ) || 0;
  }, [auditReport]);

  const handleDownloadPDF = async () => {
    const toastId = "audit-report";
    toast.loading("Generating PDF...", { id: toastId });

    try {
      const firstName = patient?.user?.first_name || "Patient";
      const lastName = patient?.user?.last_name || "";
      const patientName = `${firstName} ${lastName}`;

      const [year, month] = date ? date.split("-") : [undefined, undefined];
      const fileName = buildReportFilename({
        year,
        month,
        patientFirstName: firstName,
        patientLastName: lastName,
        doctype: DOCTYPES.AUDIT,
        program: auditType === "ccm" ? "CCM" : "RPM",
        ext: "pdf",
      });

      const blob = await pdf(
        <AuditReportPDF
          patient={patient}
          auditReport={auditReport}
          date={date}
          patientName={patientName}
          formatSecondsToTime={formatSecondsToTime}
          formatReportDate={formatReportMonth}
          logo={logo}
          billingData={billingData}
          assignedDevices={assignedDevices}
          eventLog={eventLog}
          auditType={auditType}
          monthlyRpmTime={formatSecondsToTime(monthlyRpmSeconds)}
        />
      ).toBlob();

      saveAs(blob, fileName);

      toast.dismiss(toastId);
      toast.success("PDF downloaded successfully!");
    } catch (error) {
      console.error("PDF generation error:", error);
      toast.dismiss(toastId);
      toast.error("Failed to generate PDF. Please try again.");
    }
  };

  if (isLoading || isReadingLoading || isBillingLoading) {
    return (
      <div className={styles.loadingContainer}>
        <Spinner />
      </div>
    );
  }

  const patientName = `${patient?.user?.first_name} ${patient?.user?.last_name}`;
  const rpmBillings = (billingData?.rpm_billings || []).filter(b => b.status !== 'non_billable');

  return (
    <>
      <section className={styles.download_button}>
        <Button id="download-btn" onClick={handleDownloadPDF}>
          Download PDF
        </Button>
      </section>

      {/* Screen Preview */}
      <main className={styles.container} id="reading-pdf-view">
        {/* Header Section */}
        <section className={styles.header}>
          <div className={styles.logoContainer}>
            <img src={logo} alt="TOCA Health" className={styles.logo} />
            <div className={styles.headerSeparator}></div>
          </div>

          <div className={styles.headerTitleRow}>
            <div className={styles.headerLeft}>
              <span className={styles.locationName}>
                {patient?.location?.name ?? "_"}
              </span>
            </div>
          </div>
          <div className={styles.reportTitleRow}>
            <h1 className={styles.reportTitle}>
              RPM Audit Report
            </h1>
            <div className={styles.headerRight}>
              <span className={styles.reportDate}>
                {formatReportMonth(date)}
              </span>
            </div>
          </div>
        </section>

        {/* Patient Snapshot Section */}
        <section className={styles.section}>
          <div className={styles.sectionTitleWrapper}>
            <div className={styles.blueBar}></div>
            <h2 className={styles.sectionTitle}>Patient Snapshot</h2>
          </div>

          <div className={styles.gridContainer}>
            <div className={styles.gridColumn}>
              <div className={styles.fieldGroup}>
                <label className={styles.label}>Patient Name</label>
                <div className={styles.value}>{patientName}</div>
              </div>
              <div className={styles.fieldGroup}>
                <label className={styles.label}>MRN</label>
                <div className={styles.value}>{patient?.user?.id || "_"}</div>
              </div>
              <div className={styles.fieldGroup}>
                <label className={styles.label}>Deactivated On</label>
                <div className={styles.value}>--</div>
              </div>
            </div>

            <div className={styles.gridColumn}>
              <div className={styles.fieldGroup}>
                <label className={styles.label}>Patient DOB</label>
                <div className={styles.value}>
                  {formatUSADate(patient?.user?.dob)}
                </div>
              </div>
              <div className={styles.fieldGroup}>
                <label className={styles.label}>ICD-10</label>
                <div className={styles.value}>
                  {patient?.rpm_codes?.length > 0
                    ? patient.rpm_codes.join(", ")
                    : "--"}
                </div>
              </div>
              <div className={styles.fieldGroup}>
                <label className={styles.label}>Reporting Period</label>
                <div className={styles.value}>{formatReportMonth(date)}</div>
              </div>
            </div>
          </div>
        </section>

        {/* Program Details Section */}
        <section className={styles.section}>
          <div className={styles.sectionTitleWrapper}>
            <div className={styles.blueBar}></div>
            <h2 className={styles.sectionTitle}>Program Details</h2>
          </div>

          <div className={styles.gridContainer}>
            <div className={styles.gridColumn}>
              <div className={styles.fieldGroup}>
                <label className={styles.label}>Enrollment Date</label>
                <div className={styles.value}>
                  {formatUSADate(auditReport?.patient?.enrollment_date)}
                </div>
              </div>
              <div className={styles.fieldGroup}>
                <label className={styles.label}>RPM Monitoring Time</label>
                <div className={styles.value}>
                  {formatSecondsToTime(monthlyRpmSeconds)}
                </div>
              </div>
            </div>

            <div className={styles.gridColumn}>
              <div className={styles.fieldGroup}>
                <label className={styles.label}>Consent Date</label>
                <div className={styles.value}>
                  {formatUSADate(auditReport?.patient?.consent_date)}
                </div>
              </div>
              <div className={styles.fieldGroup}>
                <label className={styles.label}>Days of Readings</label>
                <div className={styles.value}>
                  {auditReport?.reading_days_count || 0}
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Device Information Section */}
        <section className={styles.section}>
          <div className={styles.sectionTitleWrapper}>
            <div className={styles.blueBar}></div>
            <h2 className={styles.sectionTitle}>Device Information</h2>
          </div>
          
          <div className={styles.tableContainer}>
            <table className={styles.table}>
              <thead>
                <tr>
                  <th style={{ width: "30%" }}>Device Type</th>
                  <th style={{ width: "30%" }}>Serial Number</th>
                  <th style={{ width: "20%" }}>Date Assigned</th>
                  <th style={{ width: "20%" }}>Status</th>
                </tr>
              </thead>
              <tbody>
                {assignedDevices?.length > 0 ? (
                  assignedDevices.map((device, index) => (
                    <tr key={index} className={index % 2 === 0 ? styles.rowEven : styles.rowOdd}>
                      <td className={styles.textAlignStart}>{device.device_type}</td>
                      <td className={styles.textAlignStart}>{device.imei || device.hardware_uuid}</td>
                      <td className={styles.textAlignStart}>{formatUSADate(device.assigned_at)}</td>
                      <td className={styles.textAlignStart}>{device.status}</td>
                    </tr>
                  ))
                ) : (
                  <tr>
                    <td colSpan="4" className={styles.emptyCell}>No assigned devices found.</td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </section>

        {/* Other Details Section */}
        <section className={styles.section}>
          <div className={styles.sectionTitleWrapper}>
            <div className={styles.blueBar}></div>
            <h2 className={styles.sectionTitle}>Other Details</h2>
          </div>

          <div className={styles.gridContainer}>
            <div className={styles.gridColumn}>
              <div className={styles.fieldGroup}>
                <label className={styles.label}>Referring Provider</label>
                <div className={styles.value}>
                  {patient?.provider?.first_name} {patient?.provider?.last_name}
                </div>
              </div>
              <div className={styles.fieldGroup}>
                <label className={styles.label}>Organization</label>
                <div className={styles.value}>
                  {patient?.location?.name || "--"}
                </div>
              </div>
              <div className={styles.fieldGroup}>
                <label className={styles.label}>Case Manager</label>
                <div className={styles.value}>
                  {patient?.case_manager?.first_name}{" "}
                  {patient?.case_manager?.last_name}
                </div>
              </div>
            </div>

            <div className={styles.gridColumn}>
              <div className={styles.fieldGroup}>
                <label className={styles.label}>Secondary Provider</label>
                <div className={styles.value}>
                  {patient?.secondary_provider || "--"}
                </div>
              </div>
              <div className={styles.fieldGroup}>
                <label className={styles.label}>Supervising Provider</label>
                <div className={styles.value}>
                    {patient?.nurse ? `${patient.nurse.first_name} ${patient.nurse.last_name}` : "--"}
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* CPT Codes Generated */}
        <section className={styles.section}>
          <div className={styles.sectionTitleWrapper}>
            <div className={styles.blueBar}></div>
            <h2 className={styles.sectionTitle}>CPT Codes Generated</h2>
          </div>

          <div className={styles.tableContainer}>
            <table className={styles.table}>
              <thead>
                <tr>
                  <th style={{width: '12%'}}>Code</th>
                  <th style={{width: '8%'}}>Units</th>
                  <th style={{width: '15%'}}>Status</th>
                  <th style={{width: '65%'}}>Description</th>
                </tr>
              </thead>
              <tbody>
                {rpmBillings.length > 0 ? (
                  rpmBillings.map((bill, index) => (
                    <tr
                      key={index}
                      className={
                        index % 2 === 0 ? styles.rowEven : styles.rowOdd
                      }
                    >
                      <td className={styles.textAlignStart}>{bill.code}</td>
                      <td className={styles.textAlignStart}>{bill.units}</td>
                      <td className={styles.textAlignStart}>{bill.status?.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase())}</td>
                      <td className={styles.textAlignStart}>{bill.description}</td>
                    </tr>
                  ))
                ) : (
                  <tr>
                    <td colSpan="4" className={styles.emptyCell}>
                      No CPT codes generated for this period.
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </section>

        {/* Monitoring Sessions Table */}
        <section className={styles.section}>
          <div className={styles.sectionTitleWrapper}>
            <div className={styles.blueBar}></div>
            <h2 className={styles.sectionTitle}>Monitoring Sessions</h2>
          </div>

          <div className={styles.tableContainer}>
            <table className={styles.table}>
              <thead>
                <tr>
                  <th>Date</th>
                  <th>Duration</th>
                  <th>Staff</th>
                </tr>
              </thead>
              <tbody>
                {auditReport?.monitoring_sessions?.length > 0 ? (
                  auditReport.monitoring_sessions.map((session, index) => {
                    const duration = auditType === "ccm"
                      ? session?.total_ccm_seconds
                      : session?.total_rpm_seconds;
                    return (
                      <tr
                        key={session.id || index}
                        className={
                          index % 2 === 0 ? styles.rowEven : styles.rowOdd
                        }
                      >
                        <td className={styles.textAlignStart}>
                          {formatNaiveDateTime(session?.created_at)}
                        </td>
                        <td className={styles.textAlignStart}>
                          {formatSecondsToTime(duration)}
                        </td>
                        <td className={styles.textAlignStart}>
                          {session?.staff_member?.full_name || session?.assigned_by_name || "--"}
                        </td>
                      </tr>
                    );
                  })
                ) : (
                  <tr>
                    <td colSpan="3" className={styles.emptyCell}>
                      No sessions recorded.
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </section>

        {/* Notes Table */}
        <section className={styles.section}>
          <div className={styles.sectionTitleWrapper}>
            <div className={styles.blueBar}></div>
            <h2 className={styles.sectionTitle}>Notes</h2>
          </div>

          <div className={styles.tableContainer}>
            <table className={styles.table}>
              <thead>
                <tr>
                  <th style={{ width: "18%", paddingRight: "10px" }}>Date</th>
                  <th style={{ width: "8%", paddingLeft: "10px", borderLeft: "1px solid #e0e0e0" }}>Type</th>
                  <th style={{ width: "49%", paddingRight: "15px" }}>Text</th>
                  <th style={{ width: "25%", paddingLeft: "15px", borderLeft: "1px solid #e0e0e0" }}>Creator</th>
                </tr>
              </thead>
              <tbody>
                {(() => {
                  // Filter notes based on auditType (rpm_note for RPM reports, ccm_note for CCM reports)
                  const filteredNotes = auditReport?.notes?.filter(note => {
                    const noteType = (note?.note_type || note?.type || '').toLowerCase();
                    if (auditType === 'rpm') {
                      return noteType === 'rpm_note' || noteType === 'rpm';
                    } else if (auditType === 'ccm') {
                      return noteType === 'ccm_note' || noteType === 'ccm';
                    }
                    return true; // Show all if no auditType specified
                  }) || [];

                  // Helper to format note type for display
                  const formatNoteType = (noteType) => {
                    if (!noteType) return "--";
                    const type = noteType.toLowerCase();
                    if (type === 'rpm_note' || type === 'rpm') return 'RPM';
                    if (type === 'ccm_note' || type === 'ccm') return 'CCM';
                    return noteType;
                  };

                  return filteredNotes.length > 0 ? (
                    filteredNotes.map((note, index) => (
                      <tr
                        key={note.id || index}
                        className={
                          index % 2 === 0 ? styles.rowEven : styles.rowOdd
                        }
                      >
                        <td className={styles.textAlignStart} style={{ paddingRight: "10px" }}>
                          {formatUSADateTime(note?.created_at)}
                        </td>
                        <td className={styles.textAlignStart} style={{ paddingLeft: "10px", borderLeft: "1px solid #e0e0e0" }}>
                          {formatNoteType(note?.note_type || note?.type)}
                        </td>
                        <td className={styles.textAlignStart} style={{ paddingRight: "15px" }}>
                          {note?.note_text}
                        </td>
                        <td className={styles.textAlignStart} style={{ paddingLeft: "15px", borderLeft: "1px solid #e0e0e0" }}>
                          {note?.assigned_by_name || note?.assigned_by || "--"}
                        </td>
                      </tr>
                    ))
                  ) : (
                    <tr>
                      <td colSpan="4" className={styles.emptyCell}>
                        No {auditType?.toUpperCase() || ''} notes available.
                      </td>
                    </tr>
                  );
                })()}
              </tbody>
            </table>
          </div>
        </section>

        {/* Readings Table */}
        <section className={styles.section}>
          <div className={styles.sectionTitleWrapper}>
            <div className={styles.blueBar}></div>
            <h2 className={styles.sectionTitle}>Readings</h2>
          </div>

          <div className={styles.tableContainer}>
            <table className={styles.table}>
              <thead>
                <tr>
                  <th style={{ width: "25%" }}>Date</th>
                  <th style={{ width: "25%" }}>Type</th>
                  <th style={{ width: "50%" }}>Value</th>
                </tr>
              </thead>
              <tbody>
                {auditReport?.readings?.length > 0 ? (
                  auditReport.readings.map((reading, index) => (
                    <tr
                      key={reading.id || index}
                      className={
                        index % 2 === 0 ? styles.rowEven : styles.rowOdd
                      }
                    >
                      <td className={styles.textAlignStart}>
                        {formatUSADateTime(reading?.recorded_at)}
                      </td>
                      <td className={styles.textAlignStart}>
                        {reading?.reading_type_display || reading?.reading_type}
                      </td>
                      <td className={styles.textAlignStart}>
                        {reading.systolic || reading.diastolic
                          ? `${reading.systolic ?? 0}/${
                              reading.diastolic ?? 0
                            } mm Hg${reading.heart_rate ? ` / ${reading.heart_rate} bpm` : ""}`
                          : reading.weight
                          ? `${reading.weight} kg`
                          : reading.glucose
                          ? `${reading.glucose} mg/dl`
                          : reading.temperature
                          ? `${reading.temperature} F`
                          : reading.oxygen
                          ? `${reading.oxygen}% / ${reading.heart_rate} bpm`
                          : reading.value_1 || "-"}
                      </td>
                    </tr>
                  ))
                ) : (
                  <tr>
                    <td colSpan="3" className={styles.emptyCell}>
                      No readings recorded.
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </section>

        {/* Event Log Section */}
        <section className={styles.section}>
          <div className={styles.sectionTitleWrapper}>
            <div className={styles.blueBar}></div>
            <h2 className={styles.sectionTitle}>Event Log</h2>
          </div>
          <div className={styles.eventLogList}>
            {eventLog.length > 0 ? (
              eventLog.map((event, index) => (
                <div key={index} className={styles.eventLogItem}>
                  <div className={styles.eventLogDate}>
                    {formatNaiveDateTime(event.date)}
                  </div>
                  <div className={styles.eventLogContent}>
                    <div className={styles.eventLogTitle}>{event.type}</div>
                    <div className={styles.eventLogDetail}>
                      {event.details}
                    </div>
                    {event.creator && event.creator !== "Device" && event.creator !== "--" && (
                      <div className={styles.eventLogDetail} style={{fontSize: "12px", color: "#666", marginTop: "2px"}}>
                        by {event.creator}
                      </div>
                    )}
                  </div>
                </div>
              ))
            ) : (
              <p className={styles.emptyText}>No events logged.</p>
            )}
          </div>
        </section>

        {/* Watermark */}
        <img src={logo} alt="" className={styles.watermark} />

        {/* Footer */}
        <footer className={styles.footer}>
          <span>{patientName} - RPM Audit Report</span>
          <span>1 of 1</span>
          <span>
            {new Date().toLocaleString("en-US", {
              month: "2-digit",
              day: "2-digit",
              year: "numeric",
              hour: "2-digit",
              minute: "2-digit",
              hour12: true
            })}
          </span>
        </footer>
      </main>
    </>
  );
};

export default ReadingView;